import { parseSurfaceGeometry, surfaceFootprintsContact } from '../../shared/surface-geometry.js';
// janus-engine/zones/zone-membership.ts — Lane MV2 (2026-09-28): WHICH persistent zones a creature stands in, as the legacy
// walk judges it. Twins of includes/functions/janus/encounter/persistent_aoe_geometry.php:
//   collect_persistent_aoes()                 → collectPersistentZones()
//   persistent_aoe_status_is_depth_bearing()  → zoneIsDepthBearing()
//   persistent_aoe_depth_bearing_intersection → zoneContains() (FT3D floor-plane x/y + the elevation band, via Lane A's
//                                               ftShapeAoeIntersectsTarget — the same ft_shape_aoe_intersects_target twin)
//   persistent_aoe_zone_center_xy() + the wall gate in get_intersecting_paoes() → zoneWallBlocked()
// Golden vectors: tools/probes/zone_truth_geometry_vectors.php (membership family), replayed by
// tools/test/janus-zone-truth-geometry.test.mjs.
//
// Deliberately NOT ported (they hand back): a zone that is not depth-bearing (the retired side-view test
// creature_intersects_aoe), and an aoe_type outside
// the seven shapes. The walk's per-step statuses are the post-intern blob rows: no definition is attached, so every
// layer fallback PHP makes (aoe_distance / _depth / _height, range) reads null exactly as it does there.
import { ftShapeAoeIntersectsTarget, SHAPES, type AoeOccupancy } from '../../shared/aoe-target-geometry.js';
import { insideUnion, segmentHit } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { wallIntersectsCylinder, type PaintedWallGeometry } from '../../shared/painted-wall-geometry.js';
import { parsePaintedWallSectionSnapshot, paintedWallPhaseVolumes } from '../../shared/painted-wall-section-state.js';

type Row = Record<string, unknown>;
export class ZoneHandBack extends Error { constructor(public readonly reason: string) { super(reason); } }
const handBack = (reason: string): never => { throw new ZoneHandBack(reason); };
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const present = (value: unknown): boolean => { const v = scalar(value); return v !== undefined && v !== null; };
/** PHP empty(). */
const empty = (value: unknown): boolean => { const v = scalar(value); return v === undefined || v === null || v === '' || v === 0 || v === '0' || v === false; };
const f = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? n : 0; };
const SELF_RANGE = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const ENCOUNTER_TOKEN = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\|[^|,]+$/i;
const PAUSE_KEYS = ['status_paused_campaign_year', 'status_paused_campaign_month', 'status_paused_campaign_day',
  'status_paused_campaign_hour', 'status_paused_campaign_minute', 'status_paused_campaign_second'];

export interface ZoneReaders {
  /** The row's position (x, y floor plane; z elevation) — pos_coord with the lean "missing means 0". */
  position: (row: Row) => [number, number, number];
  /** ft_ground_occupancy_shape(row); undefined = the page cannot prove it (hand back). */
  occupancy: (row: Row) => AoeOccupancy | null | undefined;
  /** ft_shape_ray_z(shape, row, x, y) for the wall gate; undefined = unproved. Only read when a shape exists. */
  rayZ: (row: Row, x: number, y: number) => number | undefined;
  /** ft_spatial_row_volume(shape, row), when the lane supplies a distinct painted-wall body.
   * Otherwise occupancy already supplies the walk's physical volume; undefined = unproved. */
  spatialVolume?: (row: Row) => AoeOccupancy | undefined;
}
/** A painted wall's anchor (status_aoe_geometry): judged by its own section hazard volumes, never by an aoe shape. */
export const zoneIsPainted = (zone: Zone): boolean => zone.status.status_aoe_geometry != null && zone.status.status_aoe_geometry !== '' && !parseSurfaceGeometry(zone.status.status_aoe_geometry);
const ICE_WALL_ID = '98E982F2-46EF-1B81-87D2-6B0E8AFDE922';
/** The persisted geometry and section state, including the cast-level shape check used by stationary membership. */
export function paintedZoneSnapshot(zone: Zone): ReturnType<typeof parsePaintedWallSectionSnapshot> {
  let raw: unknown = zone.status.status_aoe_geometry;
  if (typeof raw === 'string') { if (raw.length > 150000) return null; try { raw = JSON.parse(raw); } catch { return null; } }
  // ft_painted_wall_section_snapshot() also refuses a castLevel that is not a non-negative safe integer
  const castLevel = record(raw)?.castLevel;
  if (record(raw) && 'castLevel' in record(raw)! && !(Number.isSafeInteger(castLevel) && Number(castLevel) >= 0)) return null;
  return parsePaintedWallSectionSnapshot(raw);
}
/** Canonical section phases. Keep the stationary reader's malformed-snapshot behavior: no volumes. */
export function paintedZonePhases(zone: Zone): ReturnType<typeof paintedWallPhaseVolumes> {
  const snapshot = paintedZoneSnapshot(zone);
  return snapshot ? paintedWallPhaseVolumes(snapshot.geometry, snapshot.sections) : { solid: [], residue: [], frozen: [] };
}
/** Hazard membership uses surviving solid sections, or Ice's destroyed residue. */
export function paintedZoneVolumes(zone: Zone): PaintedWallGeometry[] {
  const volumes = paintedZonePhases(zone);
  const ability = upper(zone.status.status_origin_id ?? zone.status.status_item_id);
  return ability === ICE_WALL_ID ? volumes.residue : volumes.solid;
}
/** persistent_aoe_depth_bearing_intersection()'s painted branch: ft_painted_wall_target_intersection() over the hazard volumes — the
 *  row's ft_spatial_row_volume as a cylinder (r, else the larger half-extent), optionally inflated by `grow` ft. */
export function paintedZoneContains(zone: Zone, volume: AoeOccupancy, grow = 0): boolean {
  const radius = Math.max(0, (volume.kind === 'oblong' ? Math.max(Number(volume.halfW ?? 0), Number(volume.halfD ?? 0)) : Number(volume.r ?? 0)) + grow);
  const zMin = Number(volume.zMin), zMax = Number(volume.zMax);
  if (![volume.x, volume.y, zMin, zMax, radius].every(Number.isFinite)) handBack('persistent-zone:painted-volume');
  return paintedZoneVolumes(zone).some(wall => wallIntersectsCylinder(wall, { x: Number(volume.x), y: Number(volume.y), z: zMin }, radius, zMax - zMin));
}
/** A painted zone's membership, EPS-guarded like the walk's `inside()`: a verdict that flips when the body grows or shrinks is unproved. */
function paintedZoneHolds(zone: Zone, row: Row, read: ZoneReaders, occupancy: AoeOccupancy | null): boolean {
  const volume = read.spatialVolume ? read.spatialVolume(row) : occupancy;
  if (volume == null) handBack('persistent-zone:painted-volume');
  const grown = paintedZoneContains(zone, volume!, 1e-6), shrunk = paintedZoneContains(zone, volume!, -1e-6);
  if (grown !== shrunk) handBack('persistent-zone:boundary-precision');
  return grown;
}
/** A collected zone: the status row plus, for a creature aura, its carrier's live feet (dynamic_origin_*). */
export interface Zone { status: Row; key: string; dynamic?: [number, number, number]; holderId: string }

/** status_instance_has_campaign_pause(). */
export function statusPaused(status: Row): boolean {
  for (const key of PAUSE_KEYS) {
    if (!(key in status)) continue;
    const v = scalar(status[key]);
    if (v === null || v === undefined || v === '') continue;
    if (Number.isFinite(Number(v)) && Math.trunc(Number(v)) === 0) continue;
    return true;
  }
  return false;
}
/** persistent_aoe_status_is_zone_anchor(). */
export function isZoneAnchor(status: Row): boolean {
  return Math.trunc(f(status.status_aoe_source)) === 1 && (present(status.status_aoe_distance) || present(status.status_distance_point_x));
}
const targetList = (row: Row): Row[] => rows(record(row.statuses)?.target);
const entityId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id ?? record(row.inventory)?.inventory_id);
/** The move loop's $status_key. */
export const zoneKey = (status: Row): string => String(status.status_id ?? status.status_connected_id ?? '');

/** collect_persistent_aoes(): encounter ground zones, inventory-projected zones, creature auras (carrier feet). */
export function collectPersistentZones(encounter: Row, read: ZoneReaders): Zone[] {
  const out: Zone[] = [];
  const eid = upper(record(encounter.encounters)?.encounter_id ?? encounter.encounter_id);
  for (const s of targetList(encounter)) if (!statusPaused(s) && isZoneAnchor(s)) out.push({ status: s, key: zoneKey(s), holderId: eid });
  for (const inv of rows(encounter.encounter_inventories_arr))
    for (const s of targetList(inv)) if (!statusPaused(s) && isZoneAnchor(s)) out.push({ status: s, key: zoneKey(s), holderId: entityId(inv) });
  for (const creature of rows(encounter.encounter_creatures_arr)) {
    const id = entityId(creature);
    for (const s of targetList(creature)) {
      if (statusPaused(s) || !isZoneAnchor(s) || upper(s.status_target_id) !== id) continue;
      out.push({ status: s, key: zoneKey(s), dynamic: read.position(creature), holderId: id });
    }
  }
  return out;
}

/** persistent_aoe_status_is_depth_bearing() (the dynamic recursion inlined). */
export function zoneIsDepthBearing(zone: Zone): boolean {
  const s = zone.status;
  if (!empty(s.ft3d_depth_bearing)) return true;
  if (zone.dynamic && zone.dynamic[1] !== 0) return true;
  if (!empty(s.status_origin_point_y) || !empty(s.status_distance_point_y)) return true;
  const raw = String(scalar(s.status_casting_targets_arr) ?? '').trim();
  if (raw === '') return false;
  const first = raw.includes(',') ? (raw.split(',')[0] ?? '').trim() : raw;
  if (!ENCOUNTER_TOKEN.test(first) || !first.includes(':')) return false;
  const coords = (first.split('|')[1] ?? '').split(',')[0]!.trim().split(':');
  return coords.length === 3 || coords.length === 6;
}

/** persistent_aoe_zone_center_xy(): null = no trustworthy depth (the wall gate is not judged). */
export function zoneCenterXY(zone: Zone): [number, number] | null {
  const s = zone.status;
  if (zone.dynamic) return [zone.dynamic[0], zone.dynamic[1]];
  const oy = scalar(s.status_origin_point_y), dy = scalar(s.status_distance_point_y);
  const numeric = (v: unknown): boolean => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
  if (!numeric(oy) || !numeric(dy)) return null;
  if (Number(oy) === 0 && Number(dy) === 0) return null;
  const ox = f(s.status_origin_point_x);
  const ex = present(s.status_distance_point_x) ? f(s.status_distance_point_x) : ox;
  return [(ox + ex) / 2, (Number(oy) + Number(dy)) / 2];
}

/** The zone's shape name, or a hand-back for anything the depth test does not judge. */
export function zoneShape(zone: Zone): string {
  if (zone.status.status_aoe_geometry != null && zone.status.status_aoe_geometry !== '' && !parseSurfaceGeometry(zone.status.status_aoe_geometry)) handBack('persistent-zone:painted-wall');
  const shape = SHAPES[upper(zone.status.status_aoe_type)];
  if (!shape) handBack('persistent-zone:unknown-shape');
  if (!zoneIsDepthBearing(zone)) handBack('persistent-zone:side-view');
  return shape!;
}

/** persistent_aoe_depth_bearing_intersection() for an occupancy (optionally inflated by `grow` ft). */
export function zoneContains(zone: Zone, occupancy: AoeOccupancy, grow = 0): boolean {
  if (zoneIsPainted(zone)) return paintedZoneContains(zone, occupancy, grow);
  const s = zone.status;
  const footprint = parseSurfaceGeometry(s.status_aoe_geometry)?.footprint;
  if (footprint) {
    const cx = (f(s.status_origin_point_x) + f(s.status_distance_point_x)) / 2;
    const cy = (f(s.status_origin_point_y) + f(s.status_distance_point_y)) / 2;
    const z = Math.min(f(s.status_origin_point_z), f(s.status_distance_point_z));
    const body = occupancy.kind === 'circle' ? { ...occupancy, r: Math.max(0, Number(occupancy.r ?? 0) + grow) }
      : { ...occupancy, halfW: Math.max(0, Number(occupancy.halfW ?? 0) + grow), halfD: Math.max(0, Number(occupancy.halfD ?? 0) + grow), facing: 0 };
    return surfaceFootprintsContact({ ...footprint, x: zone.dynamic?.[0] ?? cx, y: zone.dynamic?.[1] ?? cy,
      zMin: zone.dynamic?.[2] ?? z, zMax: (zone.dynamic?.[2] ?? z) + 1 }, body as Parameters<typeof surfaceFootprintsContact>[1]);
  }
  const shape = zoneShape(zone);
  let ox = f(s.status_origin_point_x), oy = f(s.status_origin_point_y), oz = f(s.status_origin_point_z);
  const hasDistance = present(s.status_distance_point_x) || present(s.status_distance_point_y) || present(s.status_distance_point_z);
  let ex = hasDistance ? f(s.status_distance_point_x) : ox, ey = hasDistance ? f(s.status_distance_point_y) : oy, ez = hasDistance ? f(s.status_distance_point_z) : oz;
  if (zone.dynamic) {
    const [cx, cy, cz] = zone.dynamic;
    const dx = cx - (ox + ex) / 2, dy = cy - (oy + ey) / 2, dz = cz - (oz + ez) / 2;
    ox += dx; oy += dy; oz += dz; ex += dx; ey += dy; ez += dz;
  }
  // The post-intern status row carries no definition: PHP's layer fallbacks read null → 0 / 1 / 1.
  let distance = f(s.status_aoe_distance); if (distance <= 0) distance = 0;
  let depth = f(s.status_aoe_distance_depth); if (depth <= 0) depth = 1;
  let height = f(s.status_aoe_distance_height); if (height <= 0) height = 1;
  const centerOnly = !empty(s.status_self_aoe_center_targets) || upper(s.range) === SELF_RANGE;
  const o: AoeOccupancy = grow === 0 ? occupancy : occupancy.kind === 'oblong'
    ? { ...occupancy, halfW: Math.max(0, Number(occupancy.halfW ?? 0) + grow), halfD: Math.max(0, Number(occupancy.halfD ?? 0) + grow) }
    : { ...occupancy, r: Math.max(0, Number(occupancy.r ?? 0) + grow) };
  if (centerOnly && Math.abs(grow) > 1e-3) handBack('persistent-zone:center-only-sampling');
  if (shape === 'cone' && Math.abs(grow) > 1e-3) handBack('persistent-zone:cone-sampling'); // the angular test ignores the body radius
  return ftShapeAoeIntersectsTarget(o, shape, distance, Math.max(1, depth), Math.max(1, height), [ox, oy, oz], [ex, ey, ez], centerOnly);
}

/** The attack-time wall gate of get_intersecting_paoes(): a zone whose centre cannot see the creature does not reach it. */
export function zoneWallBlocked(zone: Zone, shape: Row | null, row: Row, read: ZoneReaders, at?: [number, number]): boolean {
  if (shape === null) return false;
  const center = zoneCenterXY(zone);
  if (center === null) return false;
  const [tx, ty] = at ?? read.position(row);
  const z = read.rayZ(row, tx, ty);
  if (z === undefined || !Number.isFinite(z)) handBack('persistent-zone:ray-height-missing');
  const blob = shape as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!insideUnion(blob, center[0], center[1]) || !insideUnion(blob, tx, ty)) return false;
  return segmentHit(blob, center[0], center[1], tx, ty, { z, mode: 'sight', containToUnion: true }) !== null;
}

/** get_intersecting_paoes() for one creature row at its own position: the keys of the zones it stands in. */
export function intersectingZoneKeys(row: Row, zones: Zone[], shape: Row | null, read: ZoneReaders): string[] {
  const occupancy = read.occupancy(row);
  if (occupancy === undefined) handBack('persistent-zone:occupancy-missing');
  const out: string[] = [];
  for (const zone of zones) {
    if (zoneIsPainted(zone)) { if (!paintedZoneHolds(zone, row, read, occupancy!)) continue; }
    else if (occupancy === null || !zoneContains(zone, occupancy!)) continue;
    if (zoneWallBlocked(zone, shape, row, read)) continue;
    out.push(zone.key);
  }
  return out;
}
